import {
  EULER_EARN_VAULT_LENS_ADDRESS,
  VAULT_LENS_ADDRESS,
  vaultLensAbi,
  eulerEarnVaultLensAbi,
} from '@/config/euler-contracts'
import { type Address, type PublicClient, erc20Abi, formatUnits } from 'viem'

export interface AssetInfo {
  symbol: string
  decimals: number
  priceUSD: number
}

export function extractVaultLensPrice(data: unknown): number {
  if (!data) return 0
  const info = data as any
  const priceInfo = info.liabilityPriceInfo
  if (!priceInfo || priceInfo.queryFailure) return 0
  const decimals = typeof info.unitOfAccountDecimals === 'bigint'
    ? Number(info.unitOfAccountDecimals)
    : (typeof info.unitOfAccountDecimals === 'number' ? info.unitOfAccountDecimals : 18)
  return Number(formatUnits(priceInfo.amountOutMid as bigint, decimals))
}

/**
 * Fetch USD price for a vault's underlying asset via VaultLens.
 *
 * Lending vaults: VaultLens reads the vault's own oracle internally.
 * Earn vaults:    EulerEarnVaultLens → find eVault strategy → VaultLens on that strategy.
 */
export async function fetchVaultLensPrice(publicClient: PublicClient, vaultAddress: Address): Promise<number> {
  // Lending vault path
  try {
    const vaultInfo = await publicClient.readContract({
      address: VAULT_LENS_ADDRESS,
      abi: vaultLensAbi,
      functionName: 'getVaultInfoFull',
      args: [vaultAddress],
    })
    const price = extractVaultLensPrice(vaultInfo)
    if (price > 0) return price
  } catch {
    // VaultLens failed — likely an Earn vault
  }

  // Earn vault path: find a lending strategy, use its VaultLens price
  try {
    const earnInfo = await publicClient.readContract({
      address: EULER_EARN_VAULT_LENS_ADDRESS,
      abi: eulerEarnVaultLensAbi,
      functionName: 'getVaultInfoFull',
      args: [vaultAddress],
    })
    const strategies = (earnInfo as any).strategies as any[] | undefined
    const evaultStrategy = strategies?.find((s: any) => s.info?.isEVault)
    if (evaultStrategy?.strategy) {
      const strategyInfo = await publicClient.readContract({
        address: VAULT_LENS_ADDRESS,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull',
        args: [evaultStrategy.strategy as Address],
      })
      const price = extractVaultLensPrice(strategyInfo)
      if (price > 0) return price
    }
  } catch {
    // Not an Earn vault either
  }

  return 0
}

/**
 * Fetch asset information (symbol, decimals, price) for a single asset.
 * Uses VaultLens for pricing when vaultAddress is provided.
 */
export async function fetchAssetInfo(
  publicClient: PublicClient,
  assetAddress: Address,
  vaultAddress?: Address,
): Promise<AssetInfo | null> {
  try {
    const assetContracts = [
      {
        address: assetAddress,
        abi: erc20Abi,
        functionName: 'symbol' as const,
      },
      {
        address: assetAddress,
        abi: erc20Abi,
        functionName: 'decimals' as const,
      },
    ]

    const assetResults = await publicClient.multicall({ contracts: assetContracts })

    const symbol = assetResults[0]?.status === 'success' ? (assetResults[0].result as string) : 'UNKNOWN'
    const decimals = assetResults[1]?.status === 'success' ? (assetResults[1].result as number) : 18

    const priceUSD = vaultAddress ? await fetchVaultLensPrice(publicClient, vaultAddress) : 0

    return { symbol, decimals, priceUSD }
  } catch (error) {
    console.error(`Failed to fetch asset info for ${assetAddress}:`, error)
    return null
  }
}

/**
 * Batch fetch asset information for multiple assets.
 * Uses VaultLens for pricing when vaultAddresses are provided.
 */
export async function fetchBatchAssetInfo(
  publicClient: PublicClient,
  assetAddresses: Address[],
  vaultAddresses?: Address[],
): Promise<Map<Address, AssetInfo>> {
  const assetInfoMap = new Map<Address, AssetInfo>()

  if (assetAddresses.length === 0) {
    return assetInfoMap
  }

  try {
    // Fetch symbol and decimals for all assets
    const assetContracts = assetAddresses.flatMap((assetAddress) => [
      {
        address: assetAddress,
        abi: erc20Abi,
        functionName: 'symbol' as const,
      },
      {
        address: assetAddress,
        abi: erc20Abi,
        functionName: 'decimals' as const,
      },
    ])

    const assetResults = await publicClient.multicall({ contracts: assetContracts })

    // Fetch prices via VaultLens if vault addresses provided
    let vaultLensPrices: Map<string, number> | undefined
    if (vaultAddresses && vaultAddresses.length > 0) {
      vaultLensPrices = new Map()
      const lensContracts = vaultAddresses.map((vaultAddr) => ({
        address: VAULT_LENS_ADDRESS,
        abi: vaultLensAbi,
        functionName: 'getVaultInfoFull' as const,
        args: [vaultAddr] as const,
      }))

      const lensResults = await publicClient.multicall({ contracts: lensContracts })

      for (let i = 0; i < vaultAddresses.length; i++) {
        const result = lensResults[i]
        if (result?.status !== 'success' || !result.result) continue
        const vaultInfo = result.result as any
        const priceInfo = vaultInfo.liabilityPriceInfo
        if (!priceInfo || priceInfo.queryFailure) continue

        const asset = vaultInfo.asset as string | undefined
        if (!asset) continue

        const decimals = typeof vaultInfo.unitOfAccountDecimals === 'bigint'
          ? Number(vaultInfo.unitOfAccountDecimals)
          : (typeof vaultInfo.unitOfAccountDecimals === 'number' ? vaultInfo.unitOfAccountDecimals : 18)
        const priceUSD = Number(formatUnits(priceInfo.amountOutMid as bigint, decimals))
        if (priceUSD > 0) {
          vaultLensPrices.set(asset.toLowerCase(), priceUSD)
        }
      }
    }

    // Build the map
    for (let i = 0; i < assetAddresses.length; i++) {
      const baseIndex = i * 2
      const assetAddress = assetAddresses[i]

      if (!assetAddress) continue

      const symbolRes = assetResults[baseIndex]
      const decimalsRes = assetResults[baseIndex + 1]

      const symbol = symbolRes?.status === 'success' ? (symbolRes.result as string) : 'UNKNOWN'
      const decimals = decimalsRes?.status === 'success' ? (decimalsRes.result as number) : 18

      if (symbolRes?.status === 'failure') {
        console.warn(`Failed to fetch symbol for ${assetAddress}:`, symbolRes.error)
      }

      const priceUSD = vaultLensPrices?.get(assetAddress.toLowerCase()) ?? 0

      assetInfoMap.set(assetAddress, { symbol, decimals, priceUSD })
    }
  } catch (error) {
    console.error('[fetchBatchAssetInfo] Critical error:', error)
    if (error instanceof Error) {
      console.error('[fetchBatchAssetInfo] Error message:', error.message)
      console.error('[fetchBatchAssetInfo] Error stack:', error.stack)
    }
  }

  return assetInfoMap
}

/**
 * Calculate total assets in USD
 */
export function calculateTotalAssetsUSD(totalAssets: bigint, assetDecimals: number, assetPriceUSD: number): number {
  if (assetPriceUSD === 0) return 0
  const totalAssetsFormatted = Number(formatUnits(totalAssets, assetDecimals))
  return totalAssetsFormatted * assetPriceUSD
}

/**
 * Calculate supply APY from interest rate and utilization
 *
 * @param interestRate - The borrow interest rate per second (27 decimals) from the vault
 * @param utilization - Utilization ratio (0-1, e.g., 0.5 for 50%)
 * @param interestFee - Optional interest fee (e.g., 1000 = 10%, with 10000 = 100%)
 * @returns Supply APY as a percentage
 */
export function calculateSupplyAPY(interestRate: bigint, utilization: number, interestFee = 0n): number {
  const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60
  const ratePerSecond = Number(interestRate) / 1e27 // Interest rate uses 27 decimals
  const borrowAPY = ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100
  // Supply APY = Borrow APY × Utilization × (1 - Interest Fee)
  const feeMultiplier = 1 - Number(interestFee) / 10000
  return borrowAPY * utilization * feeMultiplier
}

/**
 * Calculate utilization ratio
 */
export function calculateUtilization(totalBorrows: bigint, totalAssets: bigint): number {
  if (totalAssets === 0n) return 0
  return Number(totalBorrows) / Number(totalAssets)
}

/**
 * Calculate strategy APY for EVault strategies (supply APY)
 *
 * Strategy vaults earn the supply APY when depositing into lending vaults.
 * Supply APY = Borrow APY × Utilization × (1 - Interest Fee)
 *
 * @param interestRate - The borrow interest rate per second (27 decimals) from the vault
 * @param totalBorrows - Total borrows in the vault
 * @param totalAssets - Total assets in the vault
 * @param interestFee - Optional interest fee (e.g., 1000 = 10%, with 10000 = 100%)
 * @returns Supply APY as a percentage
 */
export function calculateStrategyAPY(
  interestRate: bigint,
  totalBorrows: bigint,
  totalAssets: bigint,
  interestFee = 0n,
): number {
  if (totalAssets === 0n || interestRate === 0n) return 0

  const SECONDS_PER_YEAR = 365.25 * 24 * 60 * 60

  // Calculate utilization
  const utilization = Number(totalBorrows) / Number(totalAssets)

  // Calculate borrow APY from interest rate (rate per second with 27 decimals)
  const ratePerSecond = Number(interestRate) / 1e27
  const borrowAPY = ((1 + ratePerSecond) ** SECONDS_PER_YEAR - 1) * 100

  // Calculate supply APY (what lenders/strategy vaults earn)
  // Supply APY = Borrow APY × Utilization × (1 - Interest Fee)
  const feeMultiplier = 1 - Number(interestFee) / 10000
  const supplyAPY = borrowAPY * utilization * feeMultiplier

  return supplyAPY
}
